import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { InputGroup, Form, Table, Card, Row, Col } from "react-bootstrap";
import defaultUserImage from "../../assets/images/default-user-image.jpg";
import { IoAddCircle } from "react-icons/io5";
import { FaRegCopy, FaExternalLinkAlt, FaPhoneAlt } from "react-icons/fa";
import { FiMapPin } from "react-icons/fi";
import { MdOutlineEmail } from "react-icons/md";
import Header from "../../components/Header";
import NavBar from "../../components/NavBar";
import Footer from "../../components/Footer";
import styles from "./Organigrama.module.css";
import { Funcionario, Organica, Organigrama, SearchResult, OtherFuncionario } from "../../types/interfaces";
import { useLoading } from "../../components/LoadingContext";
import { useDebounce } from "../../hooks/usedeBounce";

const SearchComponent: React.FC = () => {
  const { setLoading } = useLoading();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const funcionarioId = params.get("funcionarioId");
  const organicaId = params.get("organicaId");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [selectedFuncionario, setSelectedFuncionario] = useState<Funcionario | null>(null);
  const [selectedOrganica, setSelectedOrganica] = useState<Organica | null>(null);
  const [organigramaAscendente, setOrganigramaAscendente] = useState<Organigrama[]>([]);
  const [organigramaDescendente, setOrganigramaDescendente] = useState<Organigrama[]>([]);
  const [otherFuncionarios, setOtherFuncionarios] = useState<OtherFuncionario[]>([]);
  const debouncedFilter = useDebounce(searchTerm, 1500);
  
    const fetchUserOrganigrama = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/organigrama/user`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
        });
        if (!response.ok) throw new Error("La respuesta de la red no fue correcta.");
        const data = await response.json();
        setSelectedFuncionario(data.funcionario || null);
        setSelectedOrganica(data.organica || null);
        setOrganigramaAscendente(data.organigramaAscendente || []);
        setOrganigramaDescendente(data.organigramaDescendente || []);
        setOtherFuncionarios(data.otherFuncionarios || []);
      } catch (error) {
        console.error("Error al obtener el organigrama del usuario:", error);
        setOrganigramaAscendente([]);
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      if (funcionarioId || organicaId) {
        if (funcionarioId) {
          handleFuncionarioClick(Number(funcionarioId));
        } else if (organicaId) {
          handleOrganicaClick(Number(organicaId));
        }
      } else {
        fetchUserOrganigrama();
      }
    }, [funcionarioId, organicaId]);

  useEffect(() => {
    if (debouncedFilter.trim()) {
      handleSearch(debouncedFilter);
    } else {
      setSearchResults([]);
    }
  }, [debouncedFilter]);

  const handleSearch = async (term: string) => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/organigrama/search?term=${encodeURIComponent(term)}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (!response.ok) throw new Error("La respuesta de la red no fue correcta.");
      const data = await response.json();
      const combinedResults: SearchResult[] = [
        ...data.funcionarios.map((funcionario: Funcionario) => ({
          funcionario,
          organica: null,
        })),
        ...data.organicas.map((organica: Organica) => ({
          funcionario: null,
          organica,
        })),
      ];
      setSearchResults(combinedResults);
    } catch (error) {
      console.error("Error al buscar:", error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFuncionarioClick = async (id: number) => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/organigrama/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (!response.ok) throw new Error("La respuesta de la red no fue correcta.");
      const data = await response.json();
      setSelectedFuncionario(data.funcionario);
      setSelectedOrganica(null);
      setOrganigramaAscendente(data.organigramaAscendente);
      setOrganigramaDescendente(data.organigramaDescendente);
      setOtherFuncionarios(data.otherFuncionarios);
      setSearchTerm(""); 
      setSearchResults([]); 
    } catch (error) {
      console.error("Error al obtener organigrama:", error);
      setOrganigramaAscendente([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOrganicaClick = async (id: number) => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/organigrama/organica/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (!response.ok) throw new Error("La respuesta de la red no fue correcta.");
      const data = await response.json();
      setSelectedOrganica(data.organica);
      setSelectedFuncionario(null);
      setOrganigramaAscendente(data.organigramaAscendente);
      setOrganigramaDescendente(data.organigramaDescendente);
      setSearchTerm(""); 
      setSearchResults([]); 
    } catch (error) {
      console.error("Error al obtener organigrama:", error);
      setOrganigramaAscendente([]);
    } finally {
      setLoading(false);
    }
  };

  const onEstablecimientoClick = async (id: number) => {
      navigate(`/establecimientos?establecimientoId=${id}`);
  };
  

  const getLastOrganica = () => {
    return organigramaAscendente.length > 0
      ? organigramaAscendente[organigramaAscendente.length - 1]
      : {
          nivel: 0,
          id: 0,
          nombre: "",
          parent_id: null,
          parent_nombre: null,
          revista: "",
          correo: "",
          descripcion: "",
          autoridad_funcionario: "",
        };
  };

  const filteredOrganigramaAscendente = organigramaAscendente.slice(0, -1);

  const getMissingSubOrganica = () => {
    const lastOrganica = getLastOrganica();
    const lastOrganicaIndex = organigramaDescendente.findIndex(
      (org) => org.id === lastOrganica.id
    );
  
    return organigramaDescendente
      .slice(lastOrganicaIndex + 1)
      .filter((org) => org.suborganica_parent !== 0);
  };

  const getMissingOrganica = () => {
    const lastOrganica = getLastOrganica();
    const lastOrganicaIndex = organigramaDescendente.findIndex(
      (org) => org.id === lastOrganica.id
    );
  
    return organigramaDescendente
      .slice(lastOrganicaIndex + 1)
      .filter((org) => org.suborganica_parent !== 1);
  };

  const OrganigramaListAsc: React.FC<{ organigrama: Organigrama[]; type: string }> = ({ organigrama, type }) => (
    <section className={styles.orgAscSection}>
      <div>
        {filteredOrganigramaAscendente.map((org, index) => (
          <div
            key={org.id}
            className={styles.orgAscContainer}
            onClick={() => handleOrganicaClick(org.id)}
          >
            {org.autoridad_funcionario && (
              <div className={styles.userContainer}>
                <img
                  src={defaultUserImage}
                  alt="User"
                  className={styles.userDisabledImage}
                />
                <p className={styles.autoridadFunc}>
                  {org.autoridad_funcionario}
                </p>
              </div>
            )}
            <p className={styles.nombreOrg}>{org.nombre}</p>
          </div>
        ))}
      </div>
    </section>
  );

  const OrganigramaListDesc: React.FC<{ organigrama: Organigrama[]; type: string }> = ({ organigrama, type }) => (
    <section className={styles.orgDescSection}>
      <Row xs={1} md={2} lg={3} xl={4} className="g-4 justify-content-center">
        {organigrama.map((org) => (
          <Col
            key={org.id}
            className={organigrama.length <= 1 ? "text-center" : ""}
          >
            <div
              className={styles.orgDescContainer}
              onClick={() => handleOrganicaClick(org.id)}
            >
              {org.autoridad_funcionario && (
                <div className={styles.userContainer}>
                  <img
                    src={defaultUserImage}
                    alt="User"
                    className={styles.userDisabledImage}
                  />
                  <p className={styles.autoridadFunc}>
                    {org.autoridad_funcionario}
                  </p>
                </div>
              )}
              <Card.Title className={styles.nombreOrg}>{org.nombre}</Card.Title>
            </div>
          </Col>
        ))}
      </Row>
    </section>
  );

  return (
    <>
      <Header />
      <NavBar />
      <div className={styles.tableContainer}>
        <h2 className={styles.titlesRoutes}>Organigrama</h2>
        <div className="d-flex justify-content-between align-items-center">
          <InputGroup className={styles.searchBarWidth}>
            <InputGroup.Text>
              <i className="pi pi-search" />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Buscar"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </div>
        {searchTerm && (
          <Table striped bordered hover className={styles.organigramTable}>
            <tbody className={styles.organigramTbody}>
              {searchResults.length > 0 ? (
                searchResults.map((result, index) => (
                  <React.Fragment key={index}>
                    {/* Para resultados de funcionario */}
                    {result.funcionario !== null &&
                      result.funcionario !== undefined && (
                        <tr
                          onClick={() =>
                            result.funcionario
                              ? handleFuncionarioClick(result.funcionario.id)
                              : null
                          }
                          className={styles.searchTable}
                        >
                          <td className={styles.organigramTableTd}>
                            <div>{result.funcionario.funcionario}</div>
                            {result.funcionario.organica && (
                              <div className={styles.nameSecondary}>
                                {result.funcionario.organica.nombre}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}

                    {/* Para resultados de orgánica */}
                    {result.organica !== null &&
                      result.organica !== undefined && (
                        <tr
                          onClick={() =>
                            result.organica
                              ? handleOrganicaClick(result.organica.id)
                              : null
                          }
                          className={styles.searchTable}
                        >
                          <td className={styles.organigramTableTd}>
                            <div>{result.organica.nombre}</div>
                            {result.organica.parent &&
                              result.organica.parent.nombre && (
                                <div className={styles.nameSecondary}>
                                  {result.organica.parent.nombre}
                                </div>
                              )}
                          </td>
                        </tr>
                      )}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan={2} className={styles.organigramTableTd}>
                    No se encontraron resultados para "{searchTerm}"
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
        {selectedFuncionario || selectedOrganica ? (
          <>
            <Card className={`mb-2 ${styles.card}`}>
              {/* Mostrar organigrama ascendente */}
              <OrganigramaListAsc
                organigrama={organigramaAscendente}
                type="Superior"
              />

              {/* Mostrar detalles de la última organica */}
              <section className={styles.selectedOrgSection}>
                <div>
                  <div className={styles.selectedOrgContainer}>
                    {getLastOrganica().autoridad_funcionario && (
                      <div className={styles.userContainer}>
                        <img
                          src={defaultUserImage}
                          alt="User"
                          className={styles.userImage}
                        />
                        <div className={styles.autoridadFunc}>
                          <p>Autoridad:</p>
                          <p>{getLastOrganica().autoridad_funcionario}</p>
                        </div>
                      </div>
                    )}
                    <p className={styles.nombreDisabledOrg}>
                      {getLastOrganica().nombre}
                    </p>
                    <div className={styles.detailsOrgContainer}>
                      <p>{getLastOrganica().correo}</p>
                      <p>{getLastOrganica().revista}</p>
                      <p>{getLastOrganica().descripcion}</p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Mostrar detalles del funcionario seleccionado */}
              {selectedFuncionario && (
                <section className={styles.selectedFuncionarioSection}>
                  <div className={styles.selectedFuncionarioContainer}>
                    <ul className={styles.selectedFuncionarioHeader}>
                      <img
                        src={defaultUserImage}
                        alt="User"
                        className={styles.userEnabledImageFuncionarios}
                      />
                      <li className={styles.selectedFuncionarioHeaderContent}>
                        <p className={styles.selectedFuncionarioName}>
                          {selectedFuncionario.funcionario}
                        </p>
                        <p className={styles.selectedFuncionarioRevista}>
                          {selectedFuncionario.revista}
                        </p>
                      </li>
                    </ul>
                    <div className={styles.selectedFuncionarioGde}>
                      <div className={styles.containerGde}>
                        <p>
                          <a
                            title={`Ir a GDE`}
                            href={`https://portal.gde.gob.ar/`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FaExternalLinkAlt />
                          </a>
                          {selectedFuncionario.usuario_gde}
                          <button
                            title={`Copiar al portapapeles`}
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(
                                selectedFuncionario.usuario_gde
                              );
                            }}
                            className={styles.copyButton}
                            aria-label="Copiar"
                          >
                            <FaRegCopy />
                          </button>
                        </p>
                      </div>
                      <div className={styles.containerGde}>
                        <p>
                          {selectedFuncionario.reparticion_gde?.nombre}
                          <button
                            title={`Copiar al portapapeles`}
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(
                                selectedFuncionario.reparticion_gde?.nombre
                              );
                            }}
                            className={styles.copyButton}
                            aria-label="Copiar"
                          >
                            <FaRegCopy />
                          </button>
                        </p>
                      </div>
                      <div className={styles.containerGde}>
                        <p>
                          {selectedFuncionario.sector_gde?.nombre}
                          <button
                            title={`Copiar al portapapeles`}
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(
                                selectedFuncionario.sector_gde?.nombre
                              );
                            }}
                            className={styles.copyButton}
                            aria-label="Copiar"
                          >
                            <FaRegCopy />
                          </button>
                        </p>
                      </div>
                    </div>
                    <div className={styles.selectedFuncionarioUbi}>
                      <p>
                        <FiMapPin className={styles.iconUbi} />
                        <span className={styles.selectedFuncionarioEstablecimiento} onClick={() => onEstablecimientoClick(selectedFuncionario.establecimiento?.id)}>{selectedFuncionario.establecimiento?.nombre}</span>
                      </p>
                      <p>{selectedFuncionario.ubi_adicional}</p>
                    </div>
                    <div className={styles.selectedFuncionarioContact}>
                      <div className={styles.contactInfoPhone}>
                        <FaPhoneAlt className={styles.iconPhone} />
                        <p className={styles.selectedFuncionarioInterno}>
                          {selectedFuncionario.interno}
                        </p>
                      </div>
                      <div className={styles.contactInfoEmail}>
                        <MdOutlineEmail className={styles.iconEmail} />
                        <div className={styles.contactInfoEmailOverflow}>
                          <a
                            href={`https://outlook.office.com/owa/?path=/mail/action/compose&to=${encodeURIComponent(
                              selectedFuncionario.correo
                            )}`}
                            onClick={(e) => {
                              e.preventDefault();
                              const target = e.target as HTMLAnchorElement;
                              const href = target.getAttribute("href");
                              if (href) {
                                window.open(
                                  href,
                                  "_blank",
                                  "toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=yes, resizable=yes, width=600, height=400"
                                );
                              }
                            }}
                            className={styles.selectedFuncionarioEmail}
                            title={`Enviar correo`}
                          >
                            {selectedFuncionario.correo}
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Mostrar otros funcionarios en la misma orgánica */}
                  <section className={styles.otherFuncionariosSection}>
                    <div className={styles.funcionariosContainer}>
                      {otherFuncionarios.map((funcionario) => (
                        <div
                          key={funcionario.id}
                          onClick={() => handleFuncionarioClick(funcionario.id)}
                          className={`${styles.funcionariosListContainer}`}
                        >
                          <ul className={styles.funcionariosList}>
                            <img
                              src={defaultUserImage}
                              alt="User"
                              className={styles.userDisabledImageFuncionarios}
                            />
                            <li className={styles.funcionarioName}>
                              {funcionario.funcionario}
                            </li>
                            <IoAddCircle />
                          </ul>
                        </div>
                      ))}
                    </div>
                  </section>
                </section>
              )}

              {/* Mostrar detalles de los funcionarios en la entidad orgánica seleccionada */}
              {selectedOrganica && (
                <section className={styles.funcionariosSection}>
                  <div className={styles.funcionariosContainer}>
                    {selectedOrganica.funcionarios.map((funcionario) => (
                      <div
                        key={funcionario.id}
                        onClick={() => handleFuncionarioClick(funcionario.id)}
                        className={`${styles.funcionariosListContainer}`}
                      >
                        <ul className={styles.funcionariosList}>
                          <img
                            src={defaultUserImage}
                            alt="User"
                            className={styles.userDisabledImageFuncionarios}
                          />
                          <li className={styles.funcionarioName}>
                            {funcionario.funcionario}
                          </li>
                          <IoAddCircle />
                        </ul>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Mostrar sub sectores del organigrama descendente faltante */}
              <OrganigramaListDesc
                organigrama={getMissingSubOrganica()}
                type="Inferior"
              />

              {/* Mostrar organigrama descendente faltante */}
              <OrganigramaListDesc
                organigrama={getMissingOrganica()}
                type="Inferior"
              />
            </Card>
          </>
        ) : null}
      </div>
      <Footer />
    </>
  );
};

export default SearchComponent;
